import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { authApi } from "@/api/endpoints";
import { useAuth } from "@/auth/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Note } from "@/components/ui/Note";
import { useI18n } from "@/i18n/use-i18n";

/**
 * Landing page for the emailed activation / reset link.
 *
 * The token is validated before the form is shown, so an expired link says so
 * instead of failing only after the user has typed a password twice.
 */
export function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const { t } = useI18n();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = useQuery({
    queryKey: ["password-token", token],
    queryFn: () => authApi.checkToken(token),
    enabled: token.length > 0,
    retry: false,
  });

  const minLength = check.data?.password_min_length ?? 8;
  const isReset = check.data?.purpose === "reset";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // Checked client-side too so the user gets the answer without a round trip;
    // the backend enforces the same rules regardless.
    if (password.length < minLength) {
      setError(t("setPassword.tooShort", { count: minLength }));
      return;
    }
    if (password !== confirm) {
      setError(t("setPassword.mismatch"));
      return;
    }

    setBusy(true);
    try {
      const session = await authApi.setPassword(token, password, confirm);
      adoptSession(session.access_token, session.user);
      navigate("/", { replace: true });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("setPassword.failed"));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell>
        <h1 className="mb-1.5 text-2xl">{t("setPassword.incompleteTitle")}</h1>
        <Note tone="error">{t("setPassword.incompleteBody")}</Note>
        <BackToLogin />
      </AuthShell>
    );
  }

  if (check.isPending) {
    return (
      <AuthShell>
        <p className="text-muted">{t("setPassword.checking")}</p>
      </AuthShell>
    );
  }

  if (check.isError || !check.data?.valid) {
    return (
      <AuthShell>
        <h1 className="mb-1.5 text-2xl">{t("setPassword.invalidTitle")}</h1>
        <Note tone="error">{t("setPassword.invalidBody")}</Note>
        <BackToLogin />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="mb-1.5 text-2xl">
        {t(isReset ? "setPassword.resetTitle" : "setPassword.setTitle")}
      </h1>
      <p className="mb-6 text-[13.6px] text-muted">
        {t(isReset ? "setPassword.resetIntro" : "setPassword.setIntro")}
      </p>

      {check.data.email && (
        <div className="mb-5 rounded-[9px] bg-page px-3.5 py-2.5 text-[13px]">
          <div className="text-muted">{t("setPassword.account")}</div>
          <div className="font-semibold">{check.data.name}</div>
          <div className="font-mono text-[12px] break-all">{check.data.email}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          label={t("setPassword.field.new")}
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="mb-4"
        />
        <Field
          label={t("setPassword.field.confirm")}
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          // Only flag a mismatch once there is something to compare against.
          error={confirm && password !== confirm ? t("setPassword.mismatch") : undefined}
        />

        <p className="mt-2 text-[12.5px] text-muted">
          {t("setPassword.minHint", { count: minLength })}
        </p>

        {error && (
          <Note tone="error" className="mt-4">
            {error}
          </Note>
        )}

        <Button type="submit" busy={busy} className="mt-5 w-full">
          {t("setPassword.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}

function BackToLogin() {
  const { t } = useI18n();
  return (
    <Link
      to="/login"
      className="mt-5 block rounded-[9px] border border-line bg-surface px-4 py-2.5 text-center text-[14.5px] font-medium text-ink hover:bg-line-soft"
    >
      {t("login.backToSignin")}
    </Link>
  );
}
